import { useState, useRef } from 'react';
import { authService } from '../utils/auth.ts';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

interface PhotoUploadProps {
  onUploadSuccess: () => void;
  onCancel?: () => void;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  onUploadSuccess,
  onCancel,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList) => {
    if (files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setError('');

    try {
      // 1. プリサインドURL取得
      const response = await authService.authFetch('/photos/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
        }),
      });

      if (!response.ok) {
        throw new Error('アップロード準備に失敗しました');
      }

      const data = await response.json();

      // 2. S3に直接アップロード
      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('ファイルのアップロードに失敗しました');
      }

      // 3. アップロード完了をバックエンドに通知
      const confirmResponse = await authService.authFetch('/photos/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photoId: data.photoId,
          s3Key: data.s3Key,
          filename: data.filename,
          fileSize: file.size,
          contentType: file.type,
        }),
        invalidateCache: '/api/photos', // 写真一覧キャッシュを無効化
      });

      if (!confirmResponse.ok) {
        throw new Error('アップロード完了処理に失敗しました');
      }

      // 4. 成功処理
      onUploadSuccess();

      // ファイル入力をリセット
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'アップロードに失敗しました'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  return (
    <div>
      {/* ヘッダー */}
      {onCancel && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem',
            borderBottom: '1px solid var(--neutral-200, #e5e7eb)',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--neutral-500, #111827)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CameraAltIcon style={{ marginRight: '8px' }} />
              写真をアップロード
            </div>
          </h3>
          <button
            onClick={onCancel}
            style={{
              padding: '0.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: 'var(--neutral-500, #6b7280)',
              borderRadius: 'var(--radius-md, 0.375rem)',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* アップロードエリア */}
      <div
        style={{
          padding: '2rem',
          border: '2px dashed #ddd',
          borderRadius: '8px',
          margin: '1rem',
          textAlign: 'center',
          backgroundColor: dragActive
            ? 'var(--drag-active-bg)'
            : 'var(--bg-card)',
          borderColor: dragActive ? '#007bff' : '#ddd',
          transition: 'all 0.3s ease',
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={isUploading}
          style={{ display: 'none' }}
        />

        {isUploading ? (
          <div>
            <div
              style={{
                fontSize: '2rem',
                marginBottom: '1rem',
                color: 'var(--info)',
              }}
            >
              📤
            </div>
            <p style={{ color: 'var(--info)', fontWeight: 'bold' }}>
              アップロード中...
            </p>
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                color: dragActive
                  ? 'var(--info)'
                  : 'var(--interactive-disabled)',
              }}
            >
              <CameraAltIcon style={{ fontSize: '3rem' }} />
            </div>
            <p
              style={{
                marginBottom: '1rem',
                color: 'var(--text-secondary)',
                fontSize: '1.1rem',
              }}
            >
              写真をドラッグ&ドロップするか、クリックして選択
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--info)',
                color: 'var(--neutral-50)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              写真を選択
            </button>
          </>
        )}

        {error && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem',
              backgroundColor: 'var(--error-bg)',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: 'var(--error)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoUpload;
